import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import {
  activeScheduleOf,
  addWeeks,
  formatDate,
  occupantOf,
  refKey,
  sameRef,
  TOPIC_MAX_LENGTH,
  weekdayOnOrAfter,
  WEEKDAYS,
  type ISODate,
  type PresenterRef,
  type Session,
} from "@love-cpmd/core";
import { Avatar, Badge, cn, Input, Select } from "@love-cpmd/ui";
import { Check, ChevronsUpDown, Info, Search, UserRoundPlus } from "lucide-react";
import { useMemo, useState } from "react";
import type { World } from "../../lib/queries";

/* ───────── Presenter picker: searchable, with avatars (plan: Input Jadwal) ───────── */

interface PresenterOption {
  key: string;
  ref: PresenterRef;
  label: string;
  sub: string;
  group: "Suggested" | "CPMD members" | "Guest presenters" | "Other";
  disabled?: boolean;
  avatarSeed: string;
  avatarUrl?: string | null;
}

export interface PresenterPickerProps {
  world: World;
  value: PresenterRef | null;
  onChange: (value: PresenterRef | null) => void;
  /** Members with an upcoming schedule can't be picked (Rule 11 / Case K). */
  blockScheduled?: boolean;
  /** Ignore this session when checking Rule 11 (e.g. the session being edited). */
  ignoreSessionId?: string;
  exclude?: PresenterRef[];
  /** Member ids to list first (e.g. rotation order for replacements). */
  suggested?: string[];
  includeGuests?: boolean;
  invalid?: boolean;
  id?: string;
  placeholder?: string;
}

export function PresenterPicker({
  world,
  value,
  onChange,
  blockScheduled,
  ignoreSessionId,
  exclude = [],
  suggested = [],
  includeGuests = true,
  invalid,
  id,
  placeholder = "Search a member…",
}: PresenterPickerProps) {
  const [query, setQuery] = useState("");

  const options = useMemo<PresenterOption[]>(() => {
    const excluded = new Set(exclude.map(refKey));
    const list: PresenterOption[] = [];
    const memberOption = (m: (typeof world.presenters)[number], group: PresenterOption["group"]): PresenterOption => {
      const active = blockScheduled ? activeScheduleOf(world.sessions, m.id, world.today) : undefined;
      const blocked = active && active.id !== ignoreSessionId;
      return {
        key: `member:${m.id}`,
        ref: { kind: "member", id: m.id },
        label: m.name,
        sub: blocked ? `Scheduled ${formatDate(active!.date, "dayMonth")}` : m.memberId,
        group,
        disabled: !!blocked,
        avatarSeed: m.memberId,
        avatarUrl: m.avatarUrl,
      };
    };
    const suggestedSet = new Set(suggested);
    for (const id of suggested) {
      const m = world.membersById.get(id);
      if (m && m.active && m.role !== "guest") list.push(memberOption(m, "Suggested"));
    }
    for (const m of [...world.presenters].sort((a, b) => a.name.localeCompare(b.name))) {
      if (!suggestedSet.has(m.id)) list.push(memberOption(m, "CPMD members"));
    }
    if (includeGuests) {
      for (const g of world.guests.filter((x) => x.active)) {
        list.push({ key: `guest:${g.id}`, ref: { kind: "guest", id: g.id }, label: g.name, sub: g.note || "Guest presenter", group: "Guest presenters", avatarSeed: g.id });
      }
    }
    return list.filter((o) => !excluded.has(o.key));
  }, [world, blockScheduled, ignoreSessionId, exclude, suggested, includeGuests]);

  const selected = useMemo<PresenterOption | null>(() => {
    if (!value) return null;
    const found = options.find((o) => sameRef(o.ref, value));
    if (found) return found;
    if (value.kind === "free") return { key: refKey(value), ref: value, label: value.name, sub: "Guest presenter", group: "Other", avatarSeed: value.name };
    return null;
  }, [value, options]);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q) || o.sub.toLowerCase().includes(q)) : options;
  const freeOption: PresenterOption | null =
    q.length >= 2 && !options.some((o) => o.label.toLowerCase() === q)
      ? { key: `free:${q}`, ref: { kind: "free", name: query.trim() }, label: query.trim(), sub: "Not in the list — use this name", group: "Other", avatarSeed: q }
      : null;
  const groups = ["Suggested", "CPMD members", "Guest presenters"] as const;

  return (
    <Combobox<PresenterOption | null>
      value={selected}
      by={(a, b) => a?.key === b?.key}
      onChange={(o) => onChange(o ? o.ref : null)}
      onClose={() => setQuery("")}
      immediate
    >
      <div className="relative">
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {selected ? (
            <Avatar name={selected.label} seed={selected.avatarSeed} src={selected.avatarUrl} size="xs" />
          ) : (
            <Search className="ml-0.5 size-[18px] text-on-surface-variant" aria-hidden />
          )}
        </span>
        <ComboboxInput
          id={id}
          aria-invalid={invalid || undefined}
          placeholder={placeholder}
          autoComplete="off"
          displayValue={(o: PresenterOption | null) => o?.label ?? ""}
          onChange={(e) => setQuery(e.target.value)}
          className="h-11 w-full rounded-control bg-surface-container-low pr-10 pl-11 text-base text-on-surface ring-1 ring-transparent ring-inset placeholder:text-outline focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary-container/70 focus:outline-none aria-[invalid=true]:bg-error-container/25 aria-[invalid=true]:ring-error/60 md:text-body-md"
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-3 text-on-surface-variant" aria-label="Show presenters">
          <ChevronsUpDown className="size-4" />
        </ComboboxButton>
      </div>
      <ComboboxOptions
        anchor="bottom start"
        transition
        className="z-[90] max-h-72 w-(--input-width) overflow-y-auto rounded-xl border border-outline-variant/50 bg-surface-container-lowest p-1.5 shadow-overlay transition duration-100 ease-out [--anchor-gap:6px] empty:invisible data-closed:opacity-0"
      >
        {groups.map((g) => {
          const items = filtered.filter((o) => o.group === g);
          if (!items.length) return null;
          return (
            <div key={g}>
              <p className="px-2.5 pt-2 pb-1 text-label-sm font-bold tracking-wider text-on-surface-variant uppercase">{g}</p>
              {items.map((o) => (
                <PresenterRow key={o.key} option={o} />
              ))}
            </div>
          );
        })}
        {freeOption && (
          <ComboboxOption
            value={freeOption}
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 data-focus:bg-surface-container-low"
          >
            <span className="flex size-6 items-center justify-center rounded-full bg-surface-container text-on-surface-variant">
              <UserRoundPlus className="size-3.5" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-body-md text-on-surface">Use “{freeOption.label}”</span>
              <span className="block text-body-sm text-on-surface-variant">Presenter outside the list</span>
            </span>
          </ComboboxOption>
        )}
        {!filtered.length && !freeOption && <p className="px-3 py-4 text-center text-body-sm text-on-surface-variant">No match. Type at least 2 letters to use a custom name.</p>}
      </ComboboxOptions>
    </Combobox>
  );
}

function PresenterRow({ option }: { option: PresenterOption }) {
  return (
    <ComboboxOption
      value={option}
      disabled={option.disabled}
      className="group flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 data-disabled:cursor-not-allowed data-disabled:opacity-55 data-focus:bg-surface-container-low"
    >
      <Avatar name={option.label} seed={option.avatarSeed} src={option.avatarUrl} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-md font-medium text-on-surface">{option.label}</span>
        <span className="block truncate text-body-sm text-on-surface-variant">{option.sub}</span>
      </span>
      {option.disabled ? (
        <Badge tone="scheduled" size="xs">
          Scheduled
        </Badge>
      ) : (
        <Check className="invisible size-4 text-primary group-data-selected:visible" aria-hidden />
      )}
    </ComboboxOption>
  );
}

/* ───────── Session-day picker: only the sharing weekday, filled days disabled (Rule 12 / Case J) ───────── */

export interface SessionDayPickerProps {
  world: World;
  value: ISODate | null;
  onChange: (date: ISODate) => void;
  /** The session being moved: its own date shows as "Current" and isn't selectable. */
  currentSession?: Session;
  invalid?: boolean;
  weeks?: number;
}

export function SessionDayPicker({ world, value, onChange, currentSession, invalid, weeks: initialWeeks = 12 }: SessionDayPickerProps) {
  const [weeks, setWeeks] = useState(initialWeeks);
  const first = weekdayOnOrAfter(world.today, world.settings.weekday);
  const days = Array.from({ length: weeks }, (_, i) => addWeeks(first, i));
  const byMonth = new Map<string, ISODate[]>();
  for (const d of days) {
    const key = d.slice(0, 7);
    byMonth.set(key, [...(byMonth.get(key) ?? []), d]);
  }
  return (
    <div className={cn("flex flex-col gap-3 rounded-2xl p-3 ring-1 ring-inset", invalid ? "bg-error-container/15 ring-error/50" : "bg-surface-container-low/60 ring-outline-variant/40")}>
      <p className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
        <Info className="size-3.5 shrink-0 text-primary" aria-hidden />
        Only {WEEKDAYS[world.settings.weekday]}s are available. Filled dates show the presenter.
      </p>
      {[...byMonth.entries()].map(([month, list]) => (
        <div key={month} className="flex flex-col gap-1.5">
          <p className="text-label-sm font-bold tracking-wider text-on-surface-variant uppercase">{formatDate(`${month}-01`, "monthYear")}</p>
          <div role="radiogroup" className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {list.map((d) => {
              const occupant = occupantOf(world.sessions, d);
              const isCurrent = currentSession && occupant?.id === currentSession.id;
              const taken = !!occupant;
              const selected = value === d;
              return (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={taken}
                  onClick={() => onChange(d)}
                  className={cn(
                    "flex min-h-16 flex-col items-start justify-between rounded-xl px-2.5 py-2 text-left ring-1 ring-inset transition-all duration-150",
                    selected
                      ? "bg-primary-container text-on-primary ring-primary-container shadow-brand"
                      : taken
                        ? isCurrent
                          ? "cursor-not-allowed bg-primary-fixed/60 text-primary ring-primary-fixed-dim"
                          : "cursor-not-allowed bg-surface-container/70 text-on-surface-variant/70 ring-transparent"
                        : "bg-surface-container-lowest text-on-surface ring-outline-variant/50 hover:ring-primary-container/60 hover:shadow-card",
                  )}
                >
                  <span className="flex w-full items-baseline justify-between gap-1">
                    <span className="text-headline-sm tabular">{formatDate(d, "day")}</span>
                    <span className={cn("text-label-sm", selected ? "text-on-primary/80" : "text-on-surface-variant")}>{formatDate(d, "monthShort").split(" ")[0]}</span>
                  </span>
                  <span className={cn("w-full truncate text-[11px] font-semibold", selected ? "text-on-primary/90" : taken ? "" : "text-completed")}>
                    {isCurrent ? "Current" : taken ? world.name(occupant.presenter, "short") : "Available"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => setWeeks((w) => w + 12)} className="self-center rounded-lg px-3 py-1.5 text-label-md text-primary hover:bg-primary-soft">
        Show more dates
      </button>
    </div>
  );
}

/* ───────── Category + topic fields ───────── */

export function CategorySelect({
  world,
  value,
  onChange,
  invalid,
  id,
  allowEmpty,
}: {
  world: World;
  value: string | null;
  onChange: (id: string) => void;
  invalid?: boolean;
  id?: string;
  allowEmpty?: boolean;
}) {
  const current = value ? world.categoriesById.get(value) : undefined;
  return (
    <Select id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} invalid={invalid}>
      <option value="" disabled={!allowEmpty}>
        Choose a category…
      </option>
      {world.activeCategories.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
      {current && !current.active && <option value={current.id}>{current.name} (inactive)</option>}
    </Select>
  );
}

export function TopicInput({
  value,
  onChange,
  invalid,
  id,
  placeholder = "Can be left empty for now",
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  id?: string;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Input id={id} value={value} maxLength={TOPIC_MAX_LENGTH + 20} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} invalid={invalid} />
      <span className={cn("self-end text-label-sm tabular", value.length > TOPIC_MAX_LENGTH ? "text-error" : "text-outline")}>
        {value.length}/{TOPIC_MAX_LENGTH}
      </span>
    </div>
  );
}

/** Every member can change anyone's schedule, so dialogs always name the owner (plan: Ubah Jadwal). */
export function OwnerNote({ session, world, userId, verb = "changing" }: { session: Session; world: World; userId: string | null; verb?: string }) {
  const own = session.presenter.kind === "member" && session.presenter.id === userId;
  if (own) return null;
  return (
    <div className="flex items-start gap-2 rounded-xl bg-rescheduled-soft px-3 py-2.5 text-body-sm text-rescheduled">
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>
        You're {verb} <strong>{world.name(session.presenter, "short")}</strong>'s schedule. They'll be notified.
      </span>
    </div>
  );
}
